import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Crown, GraduationCap, Award } from "lucide-react";
import { ProfileHeaderProps } from "./types";
import { ProfileStats } from "./shared/ProfileStats";
import { ProfileActions } from "./shared/ProfileActions";
import { FollowButton } from "./shared/FollowButton";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const ProfileHeader = ({
  profile,
  subscription,
  isOwnProfile,
  isVisitorView,
  setIsVisitorView,
  setShowEditDialog,
}: ProfileHeaderProps) => {
  // Fetch current user
  const { data: currentUser } = useQuery({
    queryKey: ["currentUser"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  // Fetch certifications separately to ensure they're always up to date
  const { data: certifications } = useQuery({
    queryKey: ["certifications", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("certifications")
        .select("*")
        .eq("user_id", profile?.id)
        .order("issue_date", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!profile?.id,
  });

  return (
    <div className="flex items-start gap-20">
      {/* Left Column - Avatar and Main Info */}
      <div className="flex-1 space-y-6">
        <div className="flex items-start gap-8">
          <Avatar className="h-40 w-40 rounded-lg">
            <AvatarImage src={profile?.avatar_url} className="object-cover" />
            <AvatarFallback className="rounded-lg">{profile?.full_name?.charAt(0) || '?'}</AvatarFallback>
          </Avatar>
          
          <div className="flex-1">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-semibold">{profile?.username || 'Profile'}</h1>
              
              <ProfileActions 
                isOwnProfile={isOwnProfile}
                isVisitorView={isVisitorView}
                setIsVisitorView={setIsVisitorView}
                setShowEditDialog={setShowEditDialog}
              />
              
              {!isOwnProfile && isVisitorView && (
                <FollowButton 
                  profileId={profile.id} 
                  currentUserId={currentUser?.id}
                />
              )}
              
              {subscription?.tier === 'premium' && (
                <Badge className="bg-amber-500 text-white">
                  <Crown className="h-4 w-4 mr-1" />
                  Premium
                </Badge>
              )}
            </div>
            
            <ProfileStats 
              portfolioCount={profile?.portfolios?.length || 0}
              profileId={profile?.id}
            />
            
            <div className="mt-4 space-y-2">
              <div className="font-medium text-xl">{profile?.full_name}</div>
              {profile?.artist_categories && (
                <div className="text-sm text-primary font-medium">
                  {profile.artist_categories.name}
                </div>
              )}
              <div className="text-muted-foreground">{profile?.headline}</div>
              <div className="whitespace-pre-wrap text-sm">{profile?.bio}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column - Certificates and Education */}
      <div className="w-80 space-y-8">
        {/* Certificates Section */}
        {certifications && certifications.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Award className="h-5 w-5" />
              Certificates
            </h3>
            <div className="space-y-4">
              {certifications.map((cert) => (
                <div key={cert.id} className="space-y-1">
                  <div className="font-medium">{cert.title}</div>
                  <div className="text-sm text-muted-foreground">
                    {cert.institution} • {format(new Date(cert.issue_date), 'MMM yyyy')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Education Section */}
        {profile?.education && profile.education.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <GraduationCap className="h-5 w-5" />
              Education
            </h3>
            <div className="space-y-4">
              {profile.education.map((edu: any) => (
                <div key={edu.id} className="space-y-1">
                  <div className="font-medium">{edu.degree}</div>
                  <div className="text-sm text-muted-foreground">
                    {edu.institution} • {format(new Date(edu.start_date), 'MMM yyyy')} - {' '}
                    {edu.end_date ? format(new Date(edu.end_date), 'MMM yyyy') : 'Present'}
                  </div>
                  {edu.field_of_study && (
                    <div className="text-sm text-muted-foreground">{edu.field_of_study}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};